import { mnemonicToEntropy, validateMnemonic, wordlists } from "bip39";
import bs58check from "bs58check";

const bufToHex = (buffer: Uint8Array): string => {
  const byteArray = Uint8Array.from(buffer),
    hexParts = [];
  for (let i = 0; i < byteArray.length; i++) {
    const hex = byteArray[i].toString(16);
    const paddedHex = ("00" + hex).slice(-2);
    hexParts.push(paddedHex);
  }

  return hexParts.join("");
};

const base58encode = (payload: Uint8Array, prefixx?: Uint8Array): string => {
  if (!prefixx) {
    throw new Error("Prefix is required");
  }

  const n = new Uint8Array(prefixx.length + payload.length);
  n.set(prefixx);
  n.set(payload, prefixx.length);

  return bs58check.encode(Buffer.from(bufToHex(n), "hex"));
};

const hexToBuf = (hex: string): Uint8Array => {
  const match = hex.match(/[\da-f]{2}/gi);

  if (!match) {
    throw new Error("Invalid hex string");
  }

  return Uint8Array.from(
    match.map(function (h) {
      return parseInt(h, 16);
    })
  );
};

const mnemonicToEntropyMine = (mnemonic: string): Uint8Array =>
  hexToBuf(mnemonicToEntropy(mnemonic));

function mnemonicToSpsk(mnemonic: string): string {
  const spsk = new Uint8Array([17, 162, 224, 201]);

  return base58encode(mnemonicToEntropyMine(mnemonic), spsk);
}

const getShiftedWord = (word: string) => {
  const mnemonicWordIndexMap: Record<string, number> = {};
  wordlists.english.forEach((word, index) => (mnemonicWordIndexMap[word] = index));
  const index = mnemonicWordIndexMap[word];

  if (typeof index !== "number") {
    throw new Error("Invalid word to shift");
  }
  const checksumByte = index % 256;
  const newIndex = index - checksumByte + ((checksumByte + 128) % 256);

  return wordlists.english[newIndex];
};

const shiftChecksum = (mnemonic: string) => {
  if (mnemonic.split(" ").length !== 24) {
    throw new Error("Invalid length");
  }

  const isOriginalValid = validateMnemonic(mnemonic);

  const words = mnemonic.split(" ");
  words[23] = getShiftedWord(words[23]);
  const shiftedMnemonic = words.join(" ");

  const isShiftedValid = validateMnemonic(shiftedMnemonic);

  // Exactly one of the mnemonics should be valid
  if (isOriginalValid === isShiftedValid) {
    throw new Error("Invalid mnemonic");
  }

  return shiftedMnemonic;
};

const kukaiMnemonicToSecretKey = (mnemonic: string): string => {
  const shiftedMnemonic = shiftChecksum(mnemonic);

  return mnemonicToSpsk(shiftedMnemonic);
};

export { kukaiMnemonicToSecretKey };
